// Currency + percentage formatters. The single home for "money in, string out".
//
// `formatAed` is the shortcut for the app's primary currency; `formatMoney`
// takes any currency code; `formatPercentage` is here because percentages
// almost always travel alongside currency in this product (ROI, gains).

import { roundToTwoDecimals } from './numberUtils'

/** The app's primary currency code. On-screen amounts use `<CurrencySymbol />`; plain-text spots (placeholders, aria-labels) use this. */
export const CURRENCY_CODE = 'AED'

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })
const numberFormatter4dp = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 })

// Token prices can be sub-cent (e.g. 0.00015). Rounding to 2 decimals would
// collapse them to "0.00", so this formatter keeps whatever precision the API
// sends — grouped thousands, but never truncated decimals.
const MAX_TOKEN_PRICE_DECIMALS = 20
const tokenPriceFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: MAX_TOKEN_PRICE_DECIMALS,
})

/**
 * Format any currency: "AED 150,000", "USD 75,500".
 * Pass `withSign=true` to prefix positive amounts with "+".
 */
export const formatMoney = (currency: string, amount: number, withSign = false): string => {
  const sign = withSign && amount > 0 ? '+' : ''
  return `${sign}${currency} ${numberFormatter.format(amount)}`
}

/**
 * Amount exactly as the API sends it, sign dropped: 2179.9 → "AED 2,179.9"
 * (never rounded to whole units). The caller shows direction.
 */
export const formatUnsignedMoney = (currency: string, amount: number): string =>
  formatMoney(currency, Math.abs(amount))

/** Format an AED amount. Pass `decimals=4` for up-to-4-decimal precision (e.g. token prices). */
export const formatAed = (amount: number, decimals = 2): string => {
  if (decimals === 4) return `${CURRENCY_CODE} ${numberFormatter4dp.format(amount)}`
  return `${CURRENCY_CODE} ${numberFormatter.format(roundToTwoDecimals(amount))}`
}

/**
 * Format a token price exactly as the API sends it: "AED 0.00015", "AED 150".
 * Unlike `formatAed`, this never rounds away sub-cent precision.
 */
export const formatTokenPrice = (amount: number): string =>
  `${CURRENCY_CODE} ${tokenPriceFormatter.format(amount)}`

/** Format a percentage with sign: "+8.50%", "-2.10%". */
export const formatPercentage = (value: number): string =>
  `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
